// Re-encode a picked photo as a plain sRGB JPEG before previewing/uploading.
//
// iPhone photos are HDR (Apple gain map) in Display P3. Showing one in Chrome
// on macOS flips the display into HDR mode and shifts the colours of every
// other image on the page -- existing binder cards turned purple/pink the
// moment an add-card preview appeared, and CSS `dynamic-range-limit` did not
// stop it. Drawing through a canvas drops the gain map and converts to sRGB.
// Also caps the size: a 12MP photo is ~3MB and nothing displays it that big.
//
// Never throws: if the browser can't decode the file (e.g. HEIC in Chrome),
// the original file is returned unchanged.
const MAX_EDGE_PX = 2400;
const JPEG_QUALITY = 0.9;

// Only still photos are converted: videos pass through, and GIFs would lose
// their animation, SVGs their vectors.
const SKIP_TYPES = new Set(['image/gif', 'image/svg+xml']);

export default async function normalizeImageFile(file) {
  if (!file || typeof createImageBitmap !== 'function') return file;
  if (!file.type?.startsWith('image/') || SKIP_TYPES.has(file.type)) return file;
  try {
    // Applies EXIF orientation, so portrait photos stay portrait.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // JPEG has no alpha; without this, transparent PNG areas turn black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    if (bitmap.close) bitmap.close();

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) return file;
    const name = `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (err) {
    return file;
  }
}

// Same, for an <input multiple> FileList (or array). Returns an array.
export function normalizeImageFiles(files) {
  return Promise.all(Array.from(files || []).map(normalizeImageFile));
}
